'use client';

import { ChangeEvent, Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from 'react';

import { getAnalyticsPlugin } from '@/app/(main)/_components/logger/analytics/get-analytics-plugin';
import { searchHealthResourcesByType } from '@/app/(main)/_components/search-modal/_services/search-health-resources-by-type.service';
import { SearchResult } from '@/app/(main)/_components/search-modal/search-modal.types';
import { useDebounceValue } from '@/hooks/use-debounce.hook';
import { useIsOnMobileDevice } from '@/hooks/use-is-on-mobile-device.hook';
import { useLoading } from '@/hooks/use-loading.hook';
import { useSearchState } from '@/store/search/search.store';
import { SearchType } from '@/types/most-search-type';
import { MostSearchedResult } from '@/types/most-search-type';
import { SelectedProcedures } from '@/app/(main)/_components/search-modal/search-modal.types';
import { isVaccineSearchMode } from '@/utils/search/search-vaccine.util';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';
import { getLogger } from '@/logger/logger';

interface UseSelectMoreExamsProps {
  selectedProcedures: SelectedProcedures[];
  setSelectProcedures: Dispatch<SetStateAction<SelectedProcedures[]>>;
}

export function useSelectMoreExams({
  selectedProcedures,
  setSelectProcedures,
}: UseSelectMoreExamsProps) {
  const [searchDisplayValue, setSearchDisplayValue] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [emptySearchResults, setEmptySearchResults] = useState(false);
  const [page, setPage] = useState(1);
  const [totalResult, setTotalResult] = useState(0);
  const [showSeeSchedules, setShowSeeSchedules] = useState(true);

  const {
    isLoading: isLoadingLaboratoryProcedures,
    startLoading: startLoadingLaboratoryProcedures,
    stopLoading: stopLoadingLaboratoryProcedures,
  } = useLoading();
  const {
    isLoading: isLoadingMoreLaboratoryProcedures,
    startLoading: startLoadingMoreLaboratoryProcedures,
    stopLoading: stopLoadingMoreLaboratoryProcedures,
  } = useLoading();

  const inputRef = useRef<HTMLInputElement>(null);
  const activeRequestId = useRef(0);
  const lastTrackedSearchTerm = useRef('');
  const analyticsInputValueRef = useRef('');
  const analytics = getAnalyticsPlugin();

  const inputValue = useDebounceValue(searchDisplayValue, 400);
  const analyticsInputValue = useDebounceValue(searchDisplayValue, 1000);
  const isOnMobileDevice = useIsOnMobileDevice();

  const isVaccine = isVaccineSearchMode(useSearchState.getState().selectedSearch);
  const searchType = isVaccine ? SearchType.VACCINE : SearchType.LABORATORIAL_EXAM;

  useEffect(() => {
    analyticsInputValueRef.current = analyticsInputValue;
  }, [analyticsInputValue]);

  const fetchSearchResults = useCallback(
    async (pageNum: number, requestId: number, searchTerm: string) => {
      try {
        const { results, total } = await searchHealthResourcesByType(searchTerm, searchType, pageNum);

        if (requestId !== activeRequestId.current) {
          return;
        }

        if (pageNum === 1) {
          const isCurrentSearch = searchTerm === analyticsInputValueRef.current;
          const isNewSearchTerm = lastTrackedSearchTerm.current !== searchTerm;

          if (results.length === 0) {
            if (isCurrentSearch && isNewSearchTerm) {
              analytics.track(AnalyticsEnum.SEARCHED_PROCEDURES_MODAL_WITHOUT_RESULTS, { term: searchTerm });
              lastTrackedSearchTerm.current = searchTerm;
            }
            setEmptySearchResults(true);
            return;
          }

          if (results.length > 0 && isCurrentSearch && isNewSearchTerm) {
            analytics.track(AnalyticsEnum.SEARCHED_PROCEDURES_MODAL_WITH_RESULTS, { term: searchTerm });
            lastTrackedSearchTerm.current = searchTerm;
          }
        }

        if (pageNum > 1 && results.length === 0) {
          return;
        }

        setTotalResult(total);
        setSearchResults((prev) => [...prev, ...results]);
      } catch (error) {
        if (requestId === activeRequestId.current) {
          getLogger('SearchModal - SelectMoreExams').error('Error while fetching Search Results', {
            lastFetchedPage: pageNum,
            error,
          });
          setEmptySearchResults(true);
        }
      }
    },
    [searchType, analytics],
  );

  useEffect(() => {
    setPage(1);
    setSearchResults([]);
    setEmptySearchResults(false);

    if (inputValue.length === 0) {
      lastTrackedSearchTerm.current = '';
      return;
    }

    if (inputValue.length <= 2) {
      return;
    }

    const currentRequestId = ++activeRequestId.current;
    startLoadingLaboratoryProcedures();

    fetchSearchResults(1, currentRequestId, inputValue).finally(() => {
      if (currentRequestId === activeRequestId.current) {
        stopLoadingLaboratoryProcedures();
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue, searchType]);

  useEffect(() => {
    const el = inputRef.current;
    if (!el) {
      return;
    }

    const onFocus = () => {
      if (isOnMobileDevice) {
        setShowSeeSchedules(false);
      }
    };
    const onBlur = () => {
      if (isOnMobileDevice) {
        setShowSeeSchedules(true);
      }
    };

    el.addEventListener('focus', onFocus);
    el.addEventListener('blur', onBlur);
    return () => {
      el.removeEventListener('focus', onFocus);
      el.removeEventListener('blur', onBlur);
    };
  }, [isOnMobileDevice]);

  const handleSelectProcedure = useCallback(
    (procedure: MostSearchedResult | SearchResult | SelectedProcedures) => {
      analytics.track(AnalyticsEnum.VIEW_SEARCH_SELECT_MORE_EXAMES);
      if (selectedProcedures.some((item) => item.id === procedure.id)) {
        setSelectProcedures((prev) => prev.filter((item) => item.id !== procedure.id));
        return;
      }

      setSelectProcedures((prev) => {
        if (prev.some((item) => item.id === procedure.id)) {
          return prev;
        }
        return [
          ...prev,
          {
            id: procedure.id || '',
            code: procedure.code || '',
            displayName: procedure.displayName || '',
          },
        ];
      });
    },
    [selectedProcedures, setSelectProcedures, analytics],
  );

  const isChecked = useCallback(
    (procedure: MostSearchedResult | SearchResult | SelectedProcedures) =>
      selectedProcedures.some((item) => item.id === procedure.id),
    [selectedProcedures],
  );

  const handleSearchInput = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchDisplayValue(e.target.value);
  };

  const handleLoadMore = useCallback(
    async () => {
      const newPage = page + 1;
      const currentRequestId = activeRequestId.current;

      setPage(newPage);
      startLoadingMoreLaboratoryProcedures();

      await fetchSearchResults(newPage, currentRequestId, inputValue).finally(() => {
        if (currentRequestId === activeRequestId.current) {
          stopLoadingMoreLaboratoryProcedures();
        }
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [page, inputValue, fetchSearchResults],
  );

  const hasSearchResults = searchResults.length > 0;
  const isSearching = inputValue.length > 0;
  const hasNoMoreResults = searchResults.length === totalResult;

  return {
    searchDisplayValue,
    handleSearchInput,
    inputRef,
    searchResults,
    emptySearchResults,
    isLoadingLaboratoryProcedures,
    isLoadingMoreLaboratoryProcedures,
    handleSelectProcedure,
    isChecked,
    handleLoadMore,
    hasSearchResults,
    isSearching,
    hasNoMoreResults,
    showSeeSchedules,
    searchInputPlaceholder: isVaccine ? 'Busque pela vacina' : 'Busque pelo exame',
    addMoreExamsMessage: isVaccine ? 'Acrescente mais vacinas.' : 'Acrescente mais exames laboratoriais.',
    selectionExamsMessage: isVaccine
      ? 'Selecione as vacinas que deseja.'
      : 'Selecione os exames laboratoriais que deseja realizar.',
    showMoreExamsTitleButton: isVaccine ? 'Ver mais vacinas' : 'Ver mais exames',
    selectedExamsCount: selectedProcedures.length === 1 ? '1 selecionado' : `${selectedProcedures.length} selecionados`,
    searchType,
  };
}
